import { useEffect, useState, type JSX, type FormEvent } from 'react';
import {
  v2,
  ApiError,
  type Annotation,
  type AnnotationSeverity,
} from '@/api/v2Client';

export interface AnnotationPanelProps {
  documentId: string;
}

const SEVERITY_ICON: Record<AnnotationSeverity, string> = {
  info: 'ℹ️',
  warning: '⚠️',
  error: '⛔',
};

export function AnnotationPanel({ documentId }: AnnotationPanelProps): JSX.Element {
  const [annotations, setAnnotations] = useState<Annotation[] | null>(null);
  const [severity, setSeverity] = useState<AnnotationSeverity>('info');
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reload = (): void => {
    v2.annotations
      .list(documentId)
      .then(setAnnotations)
      .catch((err: unknown) => {
        setError(err instanceof ApiError ? err.message : 'Failed to load annotations');
      });
  };

  useEffect(() => {
    setAnnotations(null);
    setError(null);
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [documentId]);

  const handleAdd = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    if (!comment.trim()) return;
    setSubmitting(true);
    setError(null);
    try {
      await v2.annotations.create(documentId, severity, comment.trim());
      setComment('');
      setSeverity('info');
      reload();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to add annotation');
    } finally {
      setSubmitting(false);
    }
  };

  const handleResolve = async (id: string): Promise<void> => {
    try {
      await v2.annotations.resolve(documentId, id);
      reload();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to resolve');
    }
  };

  return (
    <section aria-label="Annotations">
      <h3 style={{ marginTop: 0 }}>Annotations</h3>

      <form onSubmit={handleAdd} style={{ marginBottom: 16 }}>
        <label style={{ display: 'block', marginBottom: 6 }}>
          Severity
          <select
            value={severity}
            onChange={(e) => setSeverity(e.target.value as AnnotationSeverity)}
            style={{ marginLeft: 8 }}
          >
            <option value="info">Info</option>
            <option value="warning">Warning</option>
            <option value="error">Error</option>
          </select>
        </label>
        <textarea
          aria-label="Annotation comment"
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          rows={3}
          style={{ width: '100%', padding: 8, fontFamily: 'inherit' }}
          placeholder="Comment…"
        />
        <button
          type="submit"
          disabled={submitting || !comment.trim()}
          style={{
            marginTop: 8,
            padding: '6px 14px',
            background: '#1B3A6B',
            color: '#fff',
            border: 'none',
            borderRadius: 6,
            fontWeight: 600,
            cursor: submitting ? 'wait' : 'pointer',
          }}
        >
          {submitting ? 'Adding…' : 'Add annotation'}
        </button>
      </form>

      {error && (
        <div role="alert" style={{ color: '#b91c1c', marginBottom: 12 }}>
          {error}
        </div>
      )}

      {annotations === null ? (
        <p>Loading annotations…</p>
      ) : annotations.length === 0 ? (
        <p style={{ color: '#6b7280' }}>No annotations yet.</p>
      ) : (
        <ul style={{ listStyle: 'none', padding: 0 }}>
          {annotations.map((a) => (
            <li
              key={a.Id}
              style={{
                padding: 10,
                marginBottom: 8,
                border: '1px solid #e5e7eb',
                borderRadius: 6,
                color: a.ResolvedAt ? '#6b7280' : '#111827',
                background: a.ResolvedAt ? '#f9fafb' : '#fff',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span aria-label={`severity ${a.Severity}`}>{SEVERITY_ICON[a.Severity]}</span>
                <strong>{a.Severity}</strong>
                {a.ResolvedAt && (
                  <span
                    aria-label="resolved"
                    style={{
                      fontSize: 11,
                      background: '#e5e7eb',
                      padding: '2px 6px',
                      borderRadius: 4,
                    }}
                  >
                    Resolved
                  </span>
                )}
              </div>
              <p style={{ margin: '6px 0' }}>{a.Comment}</p>
              <div style={{ fontSize: 12, color: '#6b7280' }}>
                {a.UserId} · {new Date(a.CreatedAt).toLocaleString()}
              </div>
              {!a.ResolvedAt && (
                <button
                  type="button"
                  onClick={() => handleResolve(a.Id)}
                  style={{
                    marginTop: 6,
                    padding: '4px 10px',
                    background: '#fff',
                    border: '1px solid #1B3A6B',
                    color: '#1B3A6B',
                    borderRadius: 4,
                    cursor: 'pointer',
                  }}
                >
                  Resolve
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
